import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { competenciesApi } from './competencies.api';
import type {
  Competency,
  ImportCompetenciesPayload,
  CompetencyListParams,
  CreateCompetencyPayload,
  UpdateCompetencyPayload,
} from '../types';

export function usePmsCompetencies(params?: CompetencyListParams) {
  return useQuery({
    queryKey: pmsKeys.competencies(params),
    queryFn: () => competenciesApi.list(params),
    staleTime: 5 * 60 * 1000,
    select: (d) => (Array.isArray(d) ? d : ((d as { data?: unknown })?.data ?? [])) as Competency[],
  });
}

export function useCreatePmsCompetency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCompetencyPayload) => competenciesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.competenciesAll() }),
  });
}

export function useUpdatePmsCompetency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ competencyId, data }: { competencyId: string; data: UpdateCompetencyPayload }) =>
      competenciesApi.update(competencyId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pmsKeys.competenciesAll() });
      // Toggling active/inactive can affect the designation map.
      qc.invalidateQueries({ queryKey: pmsKeys.all });
    },
  });
}

export function useTogglePmsCompetencyActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ competencyId, is_active }: { competencyId: string; is_active: boolean }) =>
      competenciesApi.update(competencyId, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.competenciesAll() }),
  });
}

export function useDeletePmsCompetency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (competencyId: string) => competenciesApi.delete(competencyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pmsKeys.competenciesAll() });
      // A deleted competency can affect map rows.
      qc.invalidateQueries({ queryKey: pmsKeys.all });
    },
  });
}

export function useImportPmsCompetencies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ImportCompetenciesPayload) => competenciesApi.import(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.competenciesAll() }),
  });
}
